import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import {
  siteComplianceApi,
  type SiteComplianceApiError,
} from '@/lib/siteComplianceApi';
import {
  SITE_COMPLIANCE_CHECKPOINT_MANAGER_ROLE,
  withRoleGuard,
} from '@/domains/siteCompliance/rbac';
import { toSubmitEvaluationResponse } from './mappers';
import {
  attachSiteComplianceMutationErrorContext,
  createSiteComplianceCorrelationId,
  getSiteComplianceCorrelationId,
  trackSiteComplianceEvent,
} from './observability';
import type {
  CheckpointDeleteMutationVariables,
  CheckpointSettingDto,
  CheckpointUpdateMutationVariables,
  SeedCheckpointsResponseDto,
  SiteComplianceMutationResult,
  SubmitEvaluationMutationVariables,
  SubmitEvaluationResponse,
  SubmitEvaluationResponseDto,
} from './types';

const getElapsedMs = (startedAt: number) => Math.round(Date.now() - startedAt);

const toMutationResult = <TData>(
  response: AxiosResponse<TData>,
  startedAt: number,
  fallbackCorrelationId: string,
): SiteComplianceMutationResult<TData> => ({
  correlationId: getSiteComplianceCorrelationId(response, fallbackCorrelationId),
  data: response.data,
  durationMs: getElapsedMs(startedAt),
  statusCode: response.status,
});

const executeSiteComplianceMutation = async <TData>(
  request: (correlationId: string) => Promise<AxiosResponse<TData>>,
) => {
  const correlationId = createSiteComplianceCorrelationId();
  const startedAt = Date.now();

  try {
    const response = await request(correlationId);
    return toMutationResult(response, startedAt, correlationId);
  } catch (error) {
    throw attachSiteComplianceMutationErrorContext({
      correlationId,
      durationMs: getElapsedMs(startedAt),
      error: error as SiteComplianceApiError,
    });
  }
};

export const submitSiteComplianceEvaluation = async ({
  siteId,
}: SubmitEvaluationMutationVariables): Promise<SubmitEvaluationResponse> => {
  const response = await siteComplianceApi.post<SubmitEvaluationResponseDto>(
    `/api/v1/site-compliance/jobs/${encodeURIComponent(siteId)}`,
    { force: true },
  );

  return toSubmitEvaluationResponse(response.data, siteId);
};

export const useSubmitSiteComplianceEvaluationMutation = (
  options?: Omit<
    UseMutationOptions<
      SubmitEvaluationResponse,
      SiteComplianceApiError,
      SubmitEvaluationMutationVariables
    >,
    'mutationFn'
  >,
) =>
  useMutation<
    SubmitEvaluationResponse,
    SiteComplianceApiError,
    SubmitEvaluationMutationVariables
  >({
    ...options,
    mutationFn: submitSiteComplianceEvaluation,
  });

export const updateSiteComplianceCheckpoint = async ({
  checkpointId,
  patch,
  version,
}: CheckpointUpdateMutationVariables) => {
  const result = await executeSiteComplianceMutation((correlationId) =>
    siteComplianceApi.put<CheckpointSettingDto>(
      `/api/v1/system/checkpoints/${encodeURIComponent(checkpointId)}`,
      patch,
      {
        headers: { 'x-correlation-id': correlationId },
        params: { version: version || undefined },
      },
    ),
  );

  trackSiteComplianceEvent('SiteComplianceCheckpointUpdated', {
    checkpointId,
    durationMs: result.durationMs,
    statusCode: result.statusCode,
  });

  return result;
};

export const seedSiteComplianceCheckpoints = async () => {
  const result = await executeSiteComplianceMutation((correlationId) =>
    siteComplianceApi.post<SeedCheckpointsResponseDto>(
      '/api/v1/system/checkpoints',
      undefined,
      {
        headers: { 'x-correlation-id': correlationId },
      },
    ),
  );

  trackSiteComplianceEvent('SiteComplianceCheckpointSeeded', {
    durationMs: result.durationMs,
    statusCode: result.statusCode,
  });

  return result;
};

export const deleteSiteComplianceCheckpoint = async ({
  checkpointId,
  id,
}: CheckpointDeleteMutationVariables) => {
  const result = await executeSiteComplianceMutation((correlationId) =>
    siteComplianceApi.delete<null>(
      `/api/v1/system/checkpoints/${encodeURIComponent(
        checkpointId,
      )}/${encodeURIComponent(id)}`,
      {
        headers: { 'x-correlation-id': correlationId },
      },
    ),
  );

  trackSiteComplianceEvent('SiteComplianceCheckpointDeleted', {
    checkpointId,
    durationMs: result.durationMs,
    statusCode: result.statusCode,
  });

  return result;
};

export const useUpdateSiteComplianceCheckpointMutation = (
  options?: Omit<
    UseMutationOptions<
      SiteComplianceMutationResult<CheckpointSettingDto>,
      SiteComplianceApiError,
      CheckpointUpdateMutationVariables
  >,
  'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    SiteComplianceMutationResult<CheckpointSettingDto>,
    SiteComplianceApiError,
    CheckpointUpdateMutationVariables
  >({
    ...options,
    mutationFn: (variables) =>
      withRoleGuard(
        SITE_COMPLIANCE_CHECKPOINT_MANAGER_ROLE,
        () => updateSiteComplianceCheckpoint(variables),
        {
          mutationName: 'updateSiteComplianceCheckpoint',
          queryClient,
        },
      ),
  });
};

export const useSeedSiteComplianceCheckpointsMutation = (
  options?: Omit<
    UseMutationOptions<
      SiteComplianceMutationResult<SeedCheckpointsResponseDto>,
      SiteComplianceApiError,
      void
    >,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    SiteComplianceMutationResult<SeedCheckpointsResponseDto>,
    SiteComplianceApiError,
    void
  >({
    ...options,
    mutationFn: () =>
      withRoleGuard(
        SITE_COMPLIANCE_CHECKPOINT_MANAGER_ROLE,
        seedSiteComplianceCheckpoints,
        {
          mutationName: 'seedSiteComplianceCheckpoints',
          queryClient,
        },
      ),
  });
};

export const useDeleteSiteComplianceCheckpointMutation = (
  options?: Omit<
    UseMutationOptions<
      SiteComplianceMutationResult<null>,
      SiteComplianceApiError,
      CheckpointDeleteMutationVariables
    >,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    SiteComplianceMutationResult<null>,
    SiteComplianceApiError,
    CheckpointDeleteMutationVariables
  >({
    ...options,
    mutationFn: (variables) =>
      withRoleGuard(
        SITE_COMPLIANCE_CHECKPOINT_MANAGER_ROLE,
        () => deleteSiteComplianceCheckpoint(variables),
        {
          mutationName: 'deleteSiteComplianceCheckpoint',
          queryClient,
        },
      ),
  });
};
